import {
  getSupabase,
  supabasePublic,
  supabaseSchema,
} from "../../../components/supabaseClient";
import {
  addLiveCredits,
  addRecordedCredits,
} from "../../../utils/creditTracker";
import { cortiLogger } from "../../../utils/cortiLogger";
import {
  bucketName,
  backupBucketName,
  supabaseUrl,
} from "../../../components/env";
import {
  createAudioMetadata,
  getMetadataFilename,
  generateSessionId,
  createMetadataBlob,
} from "../../../utils/audioMetadata";
import { trackUserEvent } from "../../../utils/userEventTracker";
import { getFileExtension } from "./fileUtils";
import { tableForPatientId } from "../../medstudent/chartSource";
import { MAX_AUDIO_UPLOAD_BYTES } from "../Constants";
import { dbForRow } from "../../../utils/rowSchema";

export const getUserSchema = async () => {
  // Use the already-parsed active schema from supabaseClient (single source of truth)
  return supabaseSchema || "public";
};

/** Path segment for rolling periodic backup object names (one storage object per session; upsert). */
export const sanitizeRollingSessionSlug = (raw) => {
  const s = String(raw || "session").trim();
  let o = "";
  const max = 96;
  for (let i = 0; i < s.length && o.length < max; i++) {
    const c = s[i];
    if (/[a-zA-Z0-9_-]/.test(c)) o += c;
    else o += "_";
  }
  return o.replace(/_+/g, "_").replace(/^_|_$/g, "") || "sess";
};

/** Same stem as periodic backup-audio rolling objects (comma/punctuation stripped, spaces → _). */
export const sanitizePatientNameForBackup = (name) => {
  if (!name || typeof name !== "string") {
    return "unknown_patient";
  }
  let sanitized = name
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  return sanitized || "unknown_patient";
};

const getCurrentAudioCount = async (supabase, patientId) => {
  try {
    const { data, error } = await supabase
      .from(tableForPatientId(patientId))
      .select("more_audios")
      .eq("id", patientId)
      .single();

    if (error) {
      console.log("No existing audio records found, starting with 0");
      return 0;
    }

    const audioCount = data?.more_audios?.length || 0;
    console.log(`Current audio count for patient ${patientId}: ${audioCount}`);
    return audioCount;
  } catch (error) {
    console.warn("Error fetching audio count:", error);
    return 0;
  }
};

const getPatientRecordById = async (supabase, patientId) => {
  try {
    const { data, error } = await supabase
      .from(tableForPatientId(patientId))
      .select("*")
      .eq("id", patientId)
      .single();

    if (error) {
      console.log("Patient record not found:", error.message);
      return null;
    }

    console.log(`Patient record found for ID ${patientId}:`, {
      EHR_ID: data?.EHR_ID,
      patient_name: data?.patient_name,
      name: data?.name,
    });

    return data;
  } catch (error) {
    console.warn("Error fetching patient record:", error);
    return null;
  }
};

/** For storage path filenames: prefer postMessage metadata, else patient row (notes-c). */
const resolvePatientDisplayNameForPath = (metadata, patientRecord) =>
  metadata?.patientName ||
  metadata?.patient_name ||
  patientRecord?.patient_name ||
  patientRecord?.name ||
  null;

/** EHR segment in audio/backup filenames: metadata from popup first, then notes-c row. */
const resolveEhrForPath = (metadata, patientRecord) => {
  const meta =
    metadata?.ehrId ?? metadata?.ehr_id ?? metadata?.EHR_ID ?? null;
  if (meta != null && String(meta).trim() !== "") {
    return String(meta).trim();
  }
  const fromRow =
    patientRecord?.EHR_ID ||
    patientRecord?.ehr_id ||
    patientRecord?.ehrId ||
    patientRecord?.EhrId ||
    null;
  if (fromRow != null && String(fromRow).trim() !== "") {
    return String(fromRow).trim();
  }
  return "unknown_ehr";
};

/** YYYY-MM-DD for storage paths (leading segment of ISO date or parse). */
const toYyyyMmDd = (raw) => {
  if (raw == null || raw === "") return null;
  const s = String(raw).trim();
  const lead = s.match(/^(\d{4}-\d{2}-\d{2})/);
  if (lead) return lead[1];
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().split("T")[0];
};

/**
 * Visit date on the patient row — same field priority as recording / messaging.
 * Used as source of truth so stale React state or postMessage cannot pick the wrong folder.
 */
const getPatientVisitDateFromRecord = (patientRecord) => {
  if (!patientRecord) return null;
  return (
    toYyyyMmDd(patientRecord.date_) ||
    toYyyyMmDd(patientRecord.scheduledDate) ||
    toYyyyMmDd(patientRecord.date) ||
    null
  );
};

/**
 * Folder {year}/{date} for backup-audio and audio uploads.
 * Prefers DB patient visit date over caller hint when both exist; logs on mismatch.
 */
const resolveStorageFolderYearDate = (patientRecord, callerDateHint) => {
  const dbDate = getPatientVisitDateFromRecord(patientRecord);
  const hint = callerDateHint ? toYyyyMmDd(callerDateHint) : null;

  if (dbDate && hint && dbDate !== hint) {
    console.warn(
      "[STORAGE_PATH] Visit date mismatch — using patient DB row, ignoring caller hint",
      { dbVisitDate: dbDate, callerHint: hint },
    );
  }

  const date = dbDate || hint;
  if (date) {
    return { year: date.slice(0, 4), date };
  }
  const now = new Date();
  return {
    year: String(now.getFullYear()),
    date: now.toISOString().split("T")[0],
  };
};

/** Async: fetch patient row then resolve folder year/date (for transcription API payload, etc.). */
export const resolveStorageFolderYearDateForPatientId = async (
  patientId,
  callerDateHint,
) => {
  if (!patientId || patientId === "unknown") {
    return resolveStorageFolderYearDate(null, callerDateHint);
  }
  const supabase = await getSupabase();
  const patientRecord = await getPatientRecordById(supabase, patientId);
  return resolveStorageFolderYearDate(patientRecord, callerDateHint);
};

/**
 * Schema/year/date + real EHR from patient DB for transcription backups (matches backup-audio paths).
 */
export const getPatientBackupPathHintsForExternalRecording = async (
  patientId,
  callerScheduledHint,
  fallbackPatientName,
  fallbackEhr,
) => {
  if (!patientId || patientId === "unknown") return null;

  let schemaName = "public";
  try {
    schemaName = await getUserSchema();
  } catch {
    schemaName = "public";
  }

  const supabase = await getSupabase();
  const patientRecord = await getPatientRecordById(supabase, patientId);
  const { year, date } = resolveStorageFolderYearDate(
    patientRecord,
    callerScheduledHint,
  );

  const rawName =
    patientRecord?.patient_name ||
    patientRecord?.name ||
    fallbackPatientName ||
    "";

  const dbRaw =
    patientRecord?.EHR_ID ||
    patientRecord?.ehr_id ||
    patientRecord?.ehrId;
  const dbEhr =
    dbRaw != null && String(dbRaw).trim() !== ""
      ? String(dbRaw).trim()
      : "";

  let fb = "";
  if (fallbackEhr != null && String(fallbackEhr).trim() !== "") {
    fb = String(fallbackEhr).trim();
  }

  const ehrId = dbEhr || fb || "unknown_ehr";

  return {
    schemaName: schemaName || "public",
    year,
    date,
    ehrId,
    rawPatientName: typeof rawName === "string" ? rawName : String(rawName),
  };
};

/**
 * Confirm an uploaded object really landed.
 *
 * The storage buckets are PRIVATE, so `/storage/v1/object/public/<bucket>/<path>`
 * answers 400 (`NoSuchBucket`) whether or not the file is there — a public HEAD
 * can only ever report failure. Verify through a short-lived signed URL instead,
 * which is how readers reach these files too (utils/signedUrls.js, AudioPlayer.js).
 */
export const verifyUploadedObject = async (bucket, path, expiresIn = 60) => {
  try {
    const supabase = await getSupabase();
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, expiresIn);
    if (error || !data?.signedUrl) {
      console.warn(
        "⚠️ [ENHANCED-UPLOAD] Could not sign uploaded file:",
        error?.message || "no signed URL returned",
      );
      return false;
    }
    const response = await fetch(data.signedUrl, { method: "HEAD" });
    if (!response.ok) {
      console.warn(
        `⚠️ [ENHANCED-UPLOAD] Uploaded file not accessible: ${response.status} ${response.statusText}`,
      );
      return false;
    }
    return true;
  } catch (accessError) {
    console.warn(
      "⚠️ [ENHANCED-UPLOAD] File accessibility check failed:",
      accessError.message,
    );
    return false;
  }
};

export const uploadAudioFile = async (
  blob,
  filename,
  bucketName,
  supabaseUrl,
  metadata = {},
  scheduledDate = null,
  filenameSuffix = "",
) => {
  let userSchema = bucketName;
  let schemaBasedFilename = "";

  try {
    const supabase = await getSupabase();

    console.log("🔍 [ENHANCED-UPLOAD] uploadAudioFile called:", {
      filename,
      bucketName,
      blobType: blob.type,
      blobSize: blob.size,
      metadata,
      timestamp: new Date().toISOString(),
    });

    console.log("🔍 [SCHEMA-UPLOAD] Step 1: Fetching user schema...");
    userSchema = await getUserSchema();
    console.log("🔍 [SCHEMA-UPLOAD] User schema:", userSchema);

    console.log(
      "🔍 [SCHEMA-UPLOAD] Step 2: Creating new schema-based folder path...",
    );

    const patientId = metadata.patientId || "unknown";

    console.log("🔍 [SCHEMA-UPLOAD] Fetching EHR_ID from patient record...");
    const patientRecord = await getPatientRecordById(supabase, patientId);
    const patientName = resolvePatientDisplayNameForPath(
      metadata,
      patientRecord,
    );
    const ehrId = resolveEhrForPath(metadata, patientRecord);

    const { year, date } = resolveStorageFolderYearDate(
      patientRecord,
      scheduledDate ||
        metadata.scheduledDate ||
        metadata.date_ ||
        metadata.scheduled_date,
    );

    console.log("🔍 [SCHEMA-UPLOAD] Patient info:", {
      patientId,
      patientName,
      ehrId,
      year,
      date,
    });

    const currentAudioCount = await getCurrentAudioCount(supabase, patientId);
    const audioNumber = currentAudioCount + 1;
    const audioNumberFormatted = `Audio${audioNumber}${filenameSuffix}`;

    const sanitizedPatientName = patientName
      ? sanitizePatientNameForBackup(patientName)
      : "unknown_patient";
    const baseFilename = `${sanitizedPatientName}-${ehrId}_${audioNumberFormatted}`;
    const audioFileExtension = getFileExtension(
      (blob && blob.type) || "audio/webm",
    );
    const finalFilename = `${baseFilename}.${audioFileExtension}`;

    console.log("🔍 [SCHEMA-UPLOAD] Generated filename info:", {
      sanitizedPatientName,
      patientId,
      ehrId,
      currentAudioCount,
      audioNumber,
      audioNumberFormatted,
      finalFilename,
    });

    const schemaName = userSchema || "public";
    schemaBasedFilename = `${schemaName}/${year}/${date}/${finalFilename}`;

    console.log(
      "🔍 [SCHEMA-UPLOAD] Schema-based filename:",
      schemaBasedFilename,
    );

    console.log("🔍 [ENHANCED-UPLOAD] Step 3: Verifying bucket permissions...");
    const finalBucketName = "audio";

    const { data: bucketTest, error: bucketError } = await supabase.storage
      .from(finalBucketName)
      .list("", { limit: 1 });

    if (bucketError) {
      console.error("❌ [ENHANCED-UPLOAD] Bucket access failed:", bucketError);
      throw new Error(
        `Storage permission error - Bucket '${finalBucketName}' not accessible: ${bucketError.message}`,
      );
    }

    console.log(
      "✅ [ENHANCED-UPLOAD] Bucket access verified for:",
      finalBucketName,
    );

    console.log("🔍 [ENHANCED-UPLOAD] Step 4: Validating blob data...");
    if (!blob || blob.size === 0) {
      throw new Error("Invalid audio blob - empty or null");
    }

    if (blob.size > MAX_AUDIO_UPLOAD_BYTES) {
      throw new Error(
        `Audio file too large: ${blob.size} bytes (max: ${MAX_AUDIO_UPLOAD_BYTES / (1024 * 1024 * 1024)}GB)`,
      );
    }

    console.log("🔍 [ENHANCED-UPLOAD] Step 5: Uploading to storage...");
    console.log("☁️ [ENHANCED-UPLOAD] Target bucket:", finalBucketName);
    console.log(
      "☁️ [ENHANCED-UPLOAD] Schema-based filename:",
      schemaBasedFilename,
    );

    const { data: storageData, error: storageError } = await supabase.storage
      .from(finalBucketName)
      .upload(schemaBasedFilename, blob, {
        cacheControl: "3600",
        upsert: true, // Changed to true to handle existing files (overwrite them)
        contentType: blob.type,
        metadata: {
          ...metadata,
          uploadedAt: new Date().toISOString(),
          originalName: schemaBasedFilename,
          fileSize: blob.size.toString(),
          verifiedUpload: true,
          bucketName: finalBucketName,
          userSchema: userSchema,
          schemaBasedPath: true,
        },
      });

    console.log("💾 [ENHANCED-UPLOAD] Storage upload result:", {
      storageData,
      storageError,
    });

    if (storageError) {
      console.error(
        "❌ [ENHANCED-UPLOAD] Storage upload failed:",
        storageError,
      );

      // Check for various "already exists" error messages from Supabase
      const errorMsg = storageError.message || "";
      const isAlreadyExistsError =
        errorMsg.includes("already exists") ||
        errorMsg.includes("duplicate") ||
        errorMsg.includes("409") ||
        errorMsg.includes("resource already exists") ||
        errorMsg.includes("The resource already exists");

      if (storageError.message.includes("permission")) {
        throw new Error(`Storage permission denied: ${storageError.message}`);
      } else if (isAlreadyExistsError) {
        // For "already exists" errors with upsert:true, this shouldn't happen,
        // but handle it gracefully by trying to update instead
        console.warn(
          "[ENHANCED-UPLOAD] File exists error despite upsert, attempting update...",
        );
        const { data: updateData, error: updateError } = await supabase.storage
          .from(finalBucketName)
          .update(schemaBasedFilename, blob, {
            cacheControl: "3600",
            contentType: blob.type,
            metadata: {
              ...metadata,
              uploadedAt: new Date().toISOString(),
              originalName: schemaBasedFilename,
              fileSize: blob.size.toString(),
              verifiedUpload: true,
              bucketName: finalBucketName,
              userSchema: userSchema,
              schemaBasedPath: true,
            },
          });
        if (updateError) {
          throw new Error(`File update failed: ${updateError.message}`);
        }
        // Use updateData as storageData if update succeeded
        console.log(
          "💾 [ENHANCED-UPLOAD] File updated successfully after exists error",
        );
      } else if (storageError.message.includes("quota")) {
        throw new Error(`Storage quota exceeded: ${storageError.message}`);
      } else {
        throw new Error(`Upload failed: ${storageError.message}`);
      }
    }

    console.log("🔍 [ENHANCED-UPLOAD] Step 6: Verifying uploaded file...");
    const fullPath = storageData.path;
    // Stored as a PUBLIC-shaped URL on purpose: it is a durable pointer to the
    // object that readers re-sign at read time (utils/signedUrls.js#toSignedUrl).
    // A signed URL must not be persisted here — it would expire in the database.
    const finalAudioUrl = `${supabaseUrl}/storage/v1/object/public/${finalBucketName}/${fullPath}`;

    const accessible = await verifyUploadedObject(finalBucketName, fullPath);
    if (accessible) {
      console.log("✅ [ENHANCED-UPLOAD] Uploaded file is accessible");
    }

    console.log("✅ [ENHANCED-UPLOAD] File uploaded successfully");
    console.log("🔗 [ENHANCED-UPLOAD] Generated URL:", finalAudioUrl);
    console.log("📁 [ENHANCED-UPLOAD] File path:", fullPath);
    console.log("🏥 [SCHEMA-UPLOAD] Schema-based organization:", {
      userSchema,
      folderPath: schemaBasedFilename,
    });

    trackUserEvent('audio_record', {
      metadata: { size_bytes: blob?.size ?? null, bucket: finalBucketName },
    });

    return {
      url: finalAudioUrl,
      path: fullPath,
      success: true,
      storageData,
      verification: {
        bucketName: finalBucketName,
        fileSize: blob.size,
        contentType: blob.type,
        uploadedAt: new Date().toISOString(),
        accessible,
        userSchema,
        schemaBasedPath: true,
      },
    };
  } catch (error) {
    console.error("❌ [ENHANCED-UPLOAD] Error uploading audio file:", error);

    const enhancedError = new Error(
      `Audio upload failed: ${error.message} | Bucket: ${
        bucketName || "audio"
      } | Schema: ${userSchema || "unknown"} | Scheduled Date: ${
        scheduledDate || "unknown"
      }`,
    );
    enhancedError.originalError = error;
    enhancedError.context = {
      bucketName: bucketName || "audio",
      filename: schemaBasedFilename || "unknown",
      userSchema: userSchema || "unknown",
      blobSize: blob?.size || 0,
      timestamp: new Date().toISOString(),
      scheduledDate: scheduledDate || "unknown",
    };

    throw enhancedError;
  }
};

// Create backup of audio chunks with schema-based path structure
export const createBackup = async (
  chunks,
  mimeType,
  filename,
  bucketNameOverride = null,
  metadata = {},
) => {
  // Declare variables in outer scope to avoid scoping issues
  let userSchema = "public";
  let schemaBasedFilename = "";

  try {
    const supabase = await getSupabase();

    console.log("🔍 [BACKUP] createBackup called:", {
      chunksCount: chunks?.length || 0,
      mimeType,
      filename,
      metadata,
      timestamp: new Date().toISOString(),
    });

    console.log("🔍 [BACKUP] Step 1: Fetching user schema...");
    userSchema = await getUserSchema();
    console.log("🔍 [BACKUP] User schema:", userSchema);

    console.log(
      "🔍 [BACKUP] Step 2: Creating schema-based backup folder path...",
    );

    const patientId = metadata.patientId || "unknown";
    const callerScheduledHint = metadata.scheduledDate || null;

    console.log("🔍 [BACKUP] Fetching EHR_ID from patient record...");
    const patientRecord = await getPatientRecordById(supabase, patientId);
    const patientName = resolvePatientDisplayNameForPath(
      metadata,
      patientRecord,
    );
    const ehrId = resolveEhrForPath(metadata, patientRecord);

    const { year, date } = resolveStorageFolderYearDate(
      patientRecord,
      callerScheduledHint,
    );

    console.log("🔍 [BACKUP] Patient info:", {
      patientId,
      patientName,
      ehrId,
      year,
      date,
    });

    const schemaName = userSchema || "public";

    const getExtensionFromMimeType = (mime) => {
      const mimeToExt = {
        "audio/webm": "webm",
        "audio/webm;codecs=opus": "webm",
        "audio/mp4": "mp4",
        "audio/wav": "wav",
        "audio/ogg": "ogg",
        "audio/ogg;codecs=opus": "ogg",
      };
      return mimeToExt[mime] || "webm";
    };

    const fileExtension = getExtensionFromMimeType(mimeType);
    const sanitizedPatientName = patientName
      ? sanitizePatientNameForBackup(patientName)
      : "unknown_patient";

    const sidFromMeta =
      [
        typeof metadata.session_id === "string" ? metadata.session_id.trim() : "",
        typeof metadata.sessionId === "string" ? metadata.sessionId.trim() : "",
      ].find((s) => s && s.length > 0) || "";

    const rollingSessionRaw = sidFromMeta;
    const isRollingPeriodic =
      metadata.source === "periodic_backup_during_recording" &&
      rollingSessionRaw.length > 0;

    let audioNumber;
    let audioNumberFormatted;
    let finalFilename;
    let baseFilename;
    let useRollingUpsert = false;
    /** Rolling only: periodic tick (1,2,3…). Sidecar `audio_index` uses clinical slot to match transcription-backups. */
    let periodicBackupTick = null;

    if (isRollingPeriodic) {
      useRollingUpsert = true;
      const sessSlug = sanitizeRollingSessionSlug(rollingSessionRaw);
      const bn = Number(metadata.backupNumber);
      periodicBackupTick =
        Number.isFinite(bn) && bn >= 1
          ? bn
          : typeof metadata.backupNumber === "number"
            ? metadata.backupNumber
            : 1;
      const clinicSource = metadata.audioNumber ?? metadata.audio_number;
      const cn = Number(clinicSource);
      const clinicalSlot =
        clinicSource != null &&
        clinicSource !== "" &&
        Number.isFinite(cn) &&
        cn >= 1
          ? cn
          : periodicBackupTick;
      audioNumber = clinicalSlot;
      audioNumberFormatted = "Rolling";
      baseFilename = `${sanitizedPatientName}-${ehrId}_Rolling_${sessSlug}`;
      finalFilename = `${baseFilename}.${fileExtension}`;
      console.log(
        "🔍 [BACKUP] Rolling periodic path — one storage object per session (upsert)",
        {
          sanitizedPatientName,
          patientId,
          ehrId,
          sessSlug,
          backupTick: periodicBackupTick,
          clinicalAudioSlot: clinicalSlot,
        },
      );
    } else {
      const currentAudioCount = await getCurrentAudioCount(supabase, patientId);
      audioNumber = currentAudioCount + 1;
      audioNumberFormatted = `Backup${audioNumber}`;
      baseFilename = `${sanitizedPatientName}-${ehrId}_${audioNumberFormatted}`;
      finalFilename = `${baseFilename}_${crypto.randomUUID()}.${fileExtension}`;
      console.log("🔍 [BACKUP] Generated filename info:", {
        sanitizedPatientName,
        patientId,
        ehrId,
        currentAudioCount,
        audioNumber,
        audioNumberFormatted,
        finalFilename,
      });
    }

    schemaBasedFilename = `${schemaName}/${year}/${date}/${finalFilename}`;

    console.log("🔍 [BACKUP] Schema-based filename:", schemaBasedFilename);

    const finalBackupBucket =
      bucketNameOverride || backupBucketName || "backup-audio";
    console.log("🔍 [BACKUP] Step 3: Using backup bucket:", finalBackupBucket);

    // Test bucket access
    const { data: bucketTest, error: bucketError } = await supabase.storage
      .from(finalBackupBucket)
      .list("", { limit: 1 });

    if (bucketError) {
      console.error("❌ [BACKUP] Bucket access failed:", bucketError);
      throw new Error(
        `Backup storage permission error - Bucket '${finalBackupBucket}' not accessible: ${bucketError.message}`,
      );
    }

    console.log("✅ [BACKUP] Bucket access verified for:", finalBackupBucket);

    console.log("🔍 [BACKUP] Step 4: Creating backup blob...");
    const backupBlob = new Blob(chunks, { type: mimeType });

    if (!backupBlob || backupBlob.size === 0) {
      throw new Error("Invalid backup blob - empty or null");
    }

    console.log("🔍 [BACKUP] Backup blob created:", {
      size: backupBlob.size,
      type: backupBlob.type,
    });

    console.log("🔍 [BACKUP] Step 5: Uploading backup to storage...");

    const { data: storageData, error: storageError } = await supabase.storage
      .from(finalBackupBucket)
      .upload(schemaBasedFilename, backupBlob, {
        cacheControl: "3600",
        upsert: useRollingUpsert,
        contentType: mimeType,
        metadata: {
          ...metadata,
          uploadedAt: new Date().toISOString(),
          originalName: schemaBasedFilename,
          fileSize: backupBlob.size.toString(),
          isBackup: true,
          backupType: "audio_chunks",
          bucketName: finalBackupBucket,
          userSchema: userSchema,
          schemaBasedPath: true,
          patientId: patientId,
          patientName: sanitizedPatientName,
          ehrId: ehrId,
          backupNumber: periodicBackupTick ?? audioNumber,
          scheduledDate: date,
        },
      });

    console.log("💾 [BACKUP] Storage upload result:", {
      storageData,
      storageError,
    });

    if (storageError) {
      console.error("❌ [BACKUP] Storage upload failed:", storageError);

      if (storageError.message.includes("permission")) {
        throw new Error(
          `Backup storage permission denied: ${storageError.message}`,
        );
      } else if (storageError.message.includes("duplicate")) {
        throw new Error(`Backup file already exists: ${storageError.message}`);
      } else if (storageError.message.includes("quota")) {
        throw new Error(
          `Backup storage quota exceeded: ${storageError.message}`,
        );
      } else {
        throw new Error(`Backup upload failed: ${storageError.message}`);
      }
    }

    const fullPath = storageData.path;
    const backupUrl = `${supabaseUrl}/storage/v1/object/public/${finalBackupBucket}/${fullPath}`;

    // Define session_id BEFORE try block so it's available for return statement
    const backupSessionId =
      sidFromMeta || `${schemaName}/${year}/${date}/${baseFilename}`;

    console.log("✅ [BACKUP] Backup uploaded successfully");
    console.log("🔗 [BACKUP] Generated URL:", backupUrl);
    console.log("📁 [BACKUP] File path:", fullPath);
    console.log("🏥 [BACKUP] Schema-based organization:", {
      userSchema,
      folderPath: schemaBasedFilename,
    });

    // Upload metadata JSON sidecar file for metadata-driven matching
    try {
      const metadataObj = createAudioMetadata({
        sessionId: backupSessionId,
        patientId: patientId,
        patientName: sanitizedPatientName,
        audioIndex: audioNumber,
        duration: metadata?.duration || 0,
        ehrId: ehrId,
        schemaName: schemaName,
        bucket: finalBackupBucket,
        storagePath: fullPath,
        audioUrl: backupUrl,
        filename: finalFilename,
      });

      const metadataFilename = getMetadataFilename(finalFilename);
      const metadataPath = `${schemaName}/${year}/${date}/${metadataFilename}`;
      const metadataBlob = createMetadataBlob(metadataObj);

      const { data: metadataData, error: metadataError } =
        await supabase.storage
          .from(finalBackupBucket)
          .upload(metadataPath, metadataBlob, {
            cacheControl: "3600",
            upsert: useRollingUpsert,
            contentType: "application/json",
            metadata: {
              isAudioMetadata: true,
              audioSessionId: metadataObj.session_id,
              audioIndex: audioNumber,
              patientId: patientId,
            },
          });

      if (metadataError) {
        console.warn(
          "⚠️ [BACKUP] Failed to upload metadata sidecar:",
          metadataError.message,
        );
      } else {
        console.log("✅ [BACKUP] Metadata sidecar uploaded:", metadataPath);
      }
    } catch (metadataUploadError) {
      console.warn(
        "⚠️ [BACKUP] Metadata sidecar upload failed (non-critical):",
        metadataUploadError.message,
      );
    }

    return {
      filename: schemaBasedFilename,
      url: backupUrl,
      path: fullPath,
      success: true,
      storageData,
      session_id: backupSessionId,
      verification: {
        bucketName: finalBackupBucket,
        fileSize: backupBlob.size,
        contentType: mimeType,
        uploadedAt: new Date().toISOString(),
        userSchema,
        schemaBasedPath: true,
        isBackup: true,
        backupNumber: periodicBackupTick ?? audioNumber,
        patientId,
        ehrId,
      },
    };
  } catch (error) {
    console.error("❌ [BACKUP] Error creating backup:", error);

    // Add context to error for better debugging
    const enhancedError = new Error(
      `Backup creation failed: ${error.message} | Bucket: ${
        backupBucketName || "backup-audio"
      } | Schema: ${userSchema || "unknown"}`,
    );
    enhancedError.originalError = error;
    enhancedError.context = {
      bucketName: backupBucketName || "backup-audio",
      filename: schemaBasedFilename || "unknown",
      userSchema: userSchema || "unknown",
      chunksCount: chunks?.length || 0,
      timestamp: new Date().toISOString(),
    };

    throw enhancedError;
  }
};

// Update database entry with new audio - Enhanced with verification
export const updateDatabaseEntry = async (
  tableName,
  targetPatientId,
  audioUrl,
  audioDuration,
  safeUpdateData = {},
  // Schema the chart row lives in. Omit for a home-schema row — the client is
  // then used untouched, byte-identical to before this parameter existed.
  schemaName = null,
) => {
  try {
    const supabase = await getSupabase();
    // getSupabase() is hard-bound to the VIEWER's schema, so an unqualified
    // update on a cross-schema chart runs against the wrong schema, matches no
    // row, and PostgREST answers 200 [] — the recording is silently lost.
    const db = dbForRow(supabase, { _schema: schemaName }, supabaseSchema);

    console.log("🔍 [ENHANCED-DB] updateDatabaseEntry called:", {
      tableName,
      targetPatientId,
      audioUrl,
      audioDuration,
      safeUpdateData,
      schemaName: schemaName || supabaseSchema,
      timestamp: new Date().toISOString(),
    });

    console.log("🔍 [ENHANCED-DB] Step 1: Fetching current patient data...");
    const { data, error: fetchError } = await db
      .from(tableName)
      .select("more_audios")
      .eq("id", targetPatientId)
      .single();

    console.log("🔍 [ENHANCED-DB] Database fetch result:", {
      data,
      fetchError,
    });

    if (fetchError) {
      console.error(
        "❌ [ENHANCED-DB] Failed to fetch existing data:",
        fetchError,
      );
      throw new Error(`Failed to fetch existing data: ${fetchError.message}`);
    }

    // Initialize more_audios as array if it doesn't exist
    const currentMoreAudios = data.more_audios || [];
    const audioCountBefore = currentMoreAudios.length;

    console.log("🎤 [ENHANCED-DB] Current state:", {
      currentAudioCount: audioCountBefore,
      audioUrl,
      audioDuration,
      safeUpdateData,
    });

    // Include metadata in the audio entry object itself (not as separate database columns)
    const newAudio = {
      name: `Recording ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString(
        [],
        { hour: "2-digit", minute: "2-digit" },
      )}`,
      description: "Audio recording from external window",
      url: audioUrl,
      duration: audioDuration,
      uploaded_at: new Date().toISOString(),
      source: safeUpdateData?.audio_source || "external_recording_window",
      recording_timestamp:
        safeUpdateData?.recording_timestamp || new Date().toISOString(),
      verified_update: safeUpdateData?.verified_update || true,
      debug_verified: true,
      // Ensure compatibility with patient page display logic
      type: "audio",
      patient_id: targetPatientId,
      recording_date: new Date().toISOString(),
      file_size: null,
      content_type: "audio/webm",
      // Ensure at minimum the 'name' field exists for patient page button display
      displayName: `Recording ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString(
        [],
        { hour: "2-digit", minute: "2-digit" },
      )}`,
    };

    console.log("🆕 [ENHANCED-DB] New audio entry created:", newAudio);

    const updatedMoreAudios = [...currentMoreAudios, newAudio];
    const audioCountAfter = updatedMoreAudios.length;

    console.log("🔍 [ENHANCED-DB] Verifying audio count change:", {
      countBefore: audioCountBefore,
      countAfter: audioCountAfter,
      expectedIncrease: 1,
      actualIncrease: audioCountAfter - audioCountBefore,
    });

    if (audioCountAfter !== audioCountBefore + 1) {
      throw new Error(
        `Database verification failed - Expected audio count to increase by 1, but got no change`,
      );
    }

    // Essential update data - only include columns that exist in the database schema
    // Filter out any metadata fields that don't exist as columns (like audio_source, recording_timestamp, verified_update)
    const validDatabaseColumns = {
      more_audios: updatedMoreAudios,
      act_time: new Date()
        .toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        })
        .slice(0, 8),
    };

    // Only include last_audio_update if it exists in the schema, otherwise skip it
    // Filter safeUpdateData to only include known valid columns
    const filteredSafeUpdateData = {};
    const knownValidColumns = ["last_updated", "last_audio_update"]; // Add other valid columns here if needed

    // Only include fields from safeUpdateData that are known valid columns
    Object.keys(safeUpdateData || {}).forEach((key) => {
      if (knownValidColumns.includes(key)) {
        filteredSafeUpdateData[key] = safeUpdateData[key];
      } else {
        // Log that we're skipping invalid columns (metadata should be stored in audio entry, not as columns)
        console.log(
          `⚠️ [ENHANCED-DB] Skipping invalid column '${key}' - metadata should be stored in audio entry object, not as database column`,
        );
      }
    });

    const essentialUpdateData = {
      ...validDatabaseColumns,
      ...filteredSafeUpdateData,
    };

    console.log("📝 [ENHANCED-DB] Essential update data:", essentialUpdateData);

    console.log("💾 [ENHANCED-DB] Step 5: Updating database...");

    const { data: updateResult, error: updateError } = await db
      .from(tableName)
      .update(essentialUpdateData)
      .eq("id", targetPatientId)
      .select("more_audios"); // Return updated record

    console.log("💾 [ENHANCED-DB] Database update result:", {
      updateResult,
      updateError,
    });

    if (updateError) {
      console.error("❌ [ENHANCED-DB] Database update failed:", updateError);
      throw new Error(`Database update failed: ${updateError.message}`);
    }

    console.log("🔍 [ENHANCED-DB] Step 6: Verifying database update...");
    const { data: verificationData, error: verificationError } = await db
      .from(tableName)
      .select("more_audios")
      .eq("id", targetPatientId)
      .single();

    if (verificationError) {
      console.error(
        "❌ [ENHANCED-DB] Verification fetch failed:",
        verificationError,
      );
      throw new Error(
        `Verification failed - could not confirm database update: ${verificationError.message}`,
      );
    }

    const finalAudioCount = verificationData.more_audios?.length || 0;
    console.log("🔍 [ENHANCED-DB] Verification result:", {
      expectedCount: audioCountAfter,
      actualCount: finalAudioCount,
      verificationSuccessful: finalAudioCount === audioCountAfter,
    });

    if (finalAudioCount !== audioCountAfter) {
      throw new Error(
        `Database verification failed - Expected ${audioCountAfter} audio entries, but found ${finalAudioCount}`,
      );
    }

    console.log("📊 [ENHANCED-DB] Audio count verification details:");

    // Log each audio entry for verification
    verificationData.more_audios.forEach((audio, index) => {
      console.log(`📊 [ENHANCED-DB] Audio ${index + 1}:`, {
        name: audio.name,
        url: audio.url,
        duration: audio.duration,
        source: audio.source,
        uploaded_at: audio.uploaded_at,
      });
    });

    // Check if the new audio is in the correct position
    const lastAudio =
      verificationData.more_audios[verificationData.more_audios.length - 1];
    if (lastAudio && lastAudio.url === audioUrl) {
      console.log("✅ [ENHANCED-DB] New audio confirmed at end of array");
    } else {
      console.warn("⚠️ [ENHANCED-DB] New audio not found at expected position");
    }

    console.log("✅ [ENHANCED-DB] Database updated and verified successfully");
    console.log("📊 [ENHANCED-DB] Final audio count:", finalAudioCount);

    return {
      success: true,
      moreAudios: updatedMoreAudios,
      updatedRecord: updateResult,
      verification: {
        audioCountBefore,
        audioCountAfter,
        finalAudioCount,
        verified: true,
        timestamp: new Date().toISOString(),
        audioEntries: verificationData.more_audios,
        lastAudioUrl: lastAudio?.url,
        verificationSuccessful:
          finalAudioCount === audioCountAfter && lastAudio?.url === audioUrl,
      },
    };
  } catch (error) {
    console.error("❌ [ENHANCED-DB] Error updating database entry:", error);
    throw error;
  }
};

// Save transcription results
/**
 * Append the live (browser-speech) transcript row for one recording session to
 * the patient's `transcripts[]`. Idempotent per session_id: the popup, the
 * AudioRecorder parent and the RecordingBubble parent may each attempt it, and
 * exactly one row must result.
 *
 * @returns {Promise<{success: boolean, skipped?: boolean, entry?: object, error?: string}>}
 */
export const appendLiveTranscriptEntry = async ({
  patientId,
  patientName,
  transcriptText,
  duration,
  audioUrl,
  sessionId,
}) => {
  try {
    if (!patientId) return { success: false, error: "Missing patientId" };

    const supabase = await getSupabase();
    const table = tableForPatientId(patientId) || "audio_notes";
    const { data: existingData, error: fetchError } = await supabase
      .from(table)
      .select("transcripts")
      .eq("id", patientId)
      .maybeSingle();

    if (fetchError) throw fetchError;

    const existingTranscripts = Array.isArray(existingData?.transcripts)
      ? existingData.transcripts
      : existingData?.transcripts
        ? [existingData.transcripts]
        : [];

    if (
      sessionId &&
      existingTranscripts.some(
        (t) => t?.is_live_transcript && t?.session_id === sessionId,
      )
    ) {
      return { success: true, skipped: true };
    }

    const uniqueId =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}`;

    const resolvedAudioUrl =
      typeof audioUrl === "string" && audioUrl.trim() ? audioUrl.trim() : "";
    const audioFileName = resolvedAudioUrl
      ? resolvedAudioUrl.split("/").pop()?.split("?")[0] || "Recording"
      : "Recording";

    const entry = {
      id: uniqueId,
      session_id: sessionId || null,
      patient_id: patientId?.toString() || "",
      name: patientName || "Unknown Patient",
      transcript: transcriptText?.trim() ? [transcriptText.trim()] : [],
      facts: [],
      soap_notes: {},
      audio_duration: duration || 0,
      audio_source: "live_transcript",
      audio_file_name: audioFileName,
      audio_url: resolvedAudioUrl,
      created_at: new Date().toISOString(),
      is_live_transcript: true,
    };

    const { error: updateError } = await supabase
      .from(table)
      .update({ transcripts: [...existingTranscripts, entry] })
      .eq("id", patientId);

    if (updateError) throw updateError;

    if (typeof window !== "undefined" && window.dispatchEvent) {
      window.dispatchEvent(new CustomEvent("transcriptionSaved"));
    }
    return { success: true, entry };
  } catch (error) {
    console.error("❌ [LIVE-TRANSCRIPT] Failed to append live transcript row:", error);
    return { success: false, error: error?.message || "Save failed" };
  }
};

export const saveTranscriptionResults = async (
  tableName,
  patientId,
  userInfo,
  prompts,
  transcriptionData,
  actualAudioDuration,
  recordingDuration,
  creditUsage,
  processingMethod,
  audioFilename,
  audioUrl,
) => {
  try {
    const supabase = await getSupabase();

    // First, get existing transcriptions
    const { data: existingData, error: fetchError } = await supabase
      .from(tableName)
      .select("transcripts")
      .eq("id", patientId)
      .single();

    if (fetchError && fetchError.code !== "PGRST116") {
      throw fetchError;
    }

    // Create new transcription data
    const newTranscription = {
      id: Date.now().toString(),
      patient_id: patientId.toString(),
      name: (userInfo[prompts[0][0]] || "").toString(),
      transcript: Array.isArray(transcriptionData.transcript)
        ? transcriptionData.transcript
        : [transcriptionData.transcript],
      facts: Array.isArray(transcriptionData.facts)
        ? transcriptionData.facts
        : [transcriptionData.facts],
      soap_notes: transcriptionData.soapNotes || {},
      audio_duration:
        actualAudioDuration > 0
          ? Math.floor(actualAudioDuration)
          : Math.floor(
              recordingDuration.minutes * 60 + recordingDuration.seconds,
            ),
      audio_source: processingMethod || "recording",
      audio_file_name: audioFilename || "",
      estimated_credit_usage: creditUsage || null,
      created_at: new Date().toISOString(),
    };

    // Prepare the updated transcripts array
    let updatedTranscripts = [];
    if (existingData && existingData.transcripts) {
      if (Array.isArray(existingData.transcripts)) {
        updatedTranscripts = [...existingData.transcripts, newTranscription];
      } else {
        updatedTranscripts = [existingData.transcripts, newTranscription];
      }
    } else {
      updatedTranscripts = [newTranscription];
    }

    // Update the transcripts column with the array
    const { error: updateError } = await supabase
      .from(tableName)
      .update({ transcripts: updatedTranscripts })
      .eq("id", patientId);

    if (updateError) {
      throw updateError;
    }

    trackUserEvent('transcript_complete', {
      metadata: {
        source: processingMethod || 'recorded_audio',
        transcript_segments: Array.isArray(transcriptionData.transcript) ? transcriptionData.transcript.length : null,
        duration_ms: actualAudioDuration > 0 ? Math.round(actualAudioDuration * 1000) : null,
        audio_filename: audioFilename || null,
      },
    });

    // Add credits to user total
    if (creditUsage) {
      try {
        if (processingMethod === "live") {
          await addLiveCredits(creditUsage);
        } else {
          await addRecordedCredits(creditUsage);
        }
      } catch (creditError) {
        console.error("Error adding credits:", creditError);
      }
    }

    // Log to corti_logs
    try {
      if (processingMethod === "live") {
        await cortiLogger.logTranscription({
          patientId: patientId,
          patientName: userInfo[prompts[0][0]] || "Unknown Patient",
          transcriptionType: "live",
          audioFileName: "live_transcription",
          audioDurationSeconds: 0,
          audioSource: "live_stream",
          audioUrl: null,
          creditUsage: creditUsage,
          transcriptLength: transcriptionData.transcript?.length || 0,
          factsExtracted: transcriptionData.facts?.length || 0,
          soapNotesGenerated: false,
          processingTimeSeconds: 0,
          status: "completed",
          metadata: {
            hasTranscript: !!transcriptionData.transcript,
            hasFacts: transcriptionData.facts?.length > 0,
          },
        });
      } else {
        await cortiLogger.logTranscription({
          patientId: patientId,
          patientName: userInfo[prompts[0][0]] || "Unknown Patient",
          transcriptionType: "recorded",
          audioFileName: audioFilename || "Live Recording",
          audioDurationSeconds:
            actualAudioDuration ||
            recordingDuration.minutes * 60 + recordingDuration.seconds,
          audioSource: "recorded_file",
          audioUrl: audioUrl,
          creditUsage: creditUsage,
          transcriptLength: transcriptionData.transcript?.join(" ").length || 0,
          factsExtracted: transcriptionData.facts?.length || 0,
          soapNotesGenerated: !!(
            transcriptionData.soapNotes?.transcript_only ||
            transcriptionData.soapNotes?.facts_only
          ),
          processingTimeSeconds: 0,
          status: "completed",
          metadata: {
            audioSource: "recorded_file",
            hasTranscript: !!transcriptionData.transcript,
            hasFacts: !!transcriptionData.facts,
            hasSoapNotes: !!transcriptionData.soapNotes,
            processingMethod: processingMethod || "rest_api",
          },
        });
      }
    } catch (logError) {
      console.error("Error logging transcription:", logError);
    }

    return {
      success: true,
      transcription: newTranscription,
    };
  } catch (error) {
    console.error("Error saving transcription results:", error);
    throw error;
  }
};

// Find patient record by name or ID
export const findPatientRecord = async (
  tableName,
  userId,
  userInfo,
  prompts,
  patientId,
) => {
  try {
    const supabase = await getSupabase();

    // If we have a direct patient ID, use it
    if (patientId) {
      const { data: patientData, error: patientError } = await supabase
        .from(tableName)
        .select("id, more_audios")
        .eq("id", patientId)
        .single();

      if (patientError) {
        throw new Error(`Patient not found: ${patientError.message}`);
      }

      return {
        id: patientData.id,
        data: patientData,
      };
    }

    // Otherwise, try to find by name
    const { data: patientData, error: patientError } = await supabase
      .from(tableName)
      .select("id, more_audios")
      .eq(prompts[0][0], userInfo[prompts[0][0]])
      .eq("user_id", userId)
      .order("created_at", { ascending: false })
      .limit(1)
      .single();

    if (patientError) {
      throw new Error(`Patient not found: ${patientError.message}`);
    }

    return {
      id: patientData.id,
      data: patientData,
    };
  } catch (error) {
    console.error("Error finding patient record:", error);
    throw error;
  }
};
